import type {
  ManualClMyPositionsResponseItem,
  ManualClNfpmPositionRow,
  ManualClPositionHistoryResponse,
} from "@/domains/validators"

import type { ManualClGraphQlService } from "../manual-cl-graphql-service"

/**
 * Thin GraphQL transport for position NFTs owned by a connected wallet. The
 * service already handles URL, proxy and schema parsing — this wrapper just
 * exists so the orchestrator can depend on a narrow, testable interface.
 */
export class ManualClPositionGraphQlTransport {
  constructor(private readonly graphQlService: ManualClGraphQlService) {}

  async loadPositionsForAddress(
    ownerAddress: string,
    chainId: number,
  ): Promise<{
    positions: Array<ManualClMyPositionsResponseItem>
    ethPriceUsd: number | undefined
  }> {
    const response = await this.graphQlService.getMyPositions(ownerAddress, chainId)
    if (!response) {
      return { positions: [], ethPriceUsd: undefined }
    }
    return {
      positions: response.positions,
      ethPriceUsd: response.bundles[0]?.ethPriceUSD,
    }
  }

  /**
   * Per-NFT rows (`NfpmPosition`) for the connected wallet. These replace the
   * on-chain NFPM enumeration and the simulated `decreaseLiquidity` balance
   * read for indexed positions. Pending fees are still on-chain-only.
   */
  async loadNfpmPositionsForAddress(
    ownerAddress: string,
    chainId: number,
  ): Promise<Array<ManualClNfpmPositionRow>> {
    const response = await this.graphQlService.getMyNfpmPositions(ownerAddress, chainId)
    return response.nfpmPositions
  }

  async loadPositionHistory(
    positionId: string | undefined,
    nfpmPositionId?: string,
  ): Promise<ManualClPositionHistoryResponse> {
    if (nfpmPositionId) {
      return await this.graphQlService.getNfpmPositionHistory(nfpmPositionId)
    }
    if (!positionId) {
      return { positionId: undefined, history: [] }
    }
    return await this.graphQlService.getPositionHistory(positionId)
  }
}
